<template>
  <v-container fluid>
    <v-row>
      <v-col cols="12">
        <div class="d-flex justify-space-between align-center mb-6">
          <div>
            <h1 class="text-h4 mb-2">Gestion des fiches de besoin</h1>
            <p class="text-subtitle-1 text-grey">
              Créez et gérez les fiches de besoin pour vos recrutements
            </p>
          </div>
          <div class="d-flex gap-2">
            <v-btn color="secondary" prepend-icon="mdi-upload" @click="uploadDialog = true">
              Importer une fiche
            </v-btn>
            <v-btn color="primary" prepend-icon="mdi-plus" @click="createNeedDialog = true">
              Nouvelle fiche de besoin
            </v-btn>
          </div>
        </div>
      </v-col>
    </v-row>

    <!-- Filters and Search -->
    <v-row class="mb-4">
      <v-col cols="12" md="4">
        <v-text-field v-model="searchTerm" prepend-inner-icon="mdi-magnify" label="Rechercher..." variant="outlined"
          density="compact" clearable></v-text-field>
      </v-col>
      <v-col cols="12" md="3">
        <v-select v-model="selectedStatus" :items="statusOptions" label="Statut" variant="outlined" density="compact"
          clearable></v-select>
      </v-col>
      <v-col cols="12" md="3">
        <v-select v-model="selectedDepartment" :items="departments" label="Département" variant="outlined"
          density="compact" clearable></v-select>
      </v-col>
      <v-col cols="12" md="2">
        <v-btn variant="outlined" block @click="resetFilters">
          Réinitialiser
        </v-btn>
      </v-col>
    </v-row>

    <!-- Needs List -->
    <v-card>
      <v-data-table :headers="headers" :items="filteredNeeds" :loading="loading" class="elevation-1" item-key="id">
        <template v-slot:item.status="{ item }">
          <v-chip :color="getStatusColor(item.status)" size="small">
            {{ getStatusText(item.status) }}
          </v-chip>
        </template>

        <template v-slot:item.priority="{ item }">
          <v-chip :color="getPriorityColor(item.priority)" size="small">
            {{ getPriorityText(item.priority) }}
          </v-chip>
        </template>

        <template v-slot:item.createdAt="{ item }">
          {{ formatDate(item.createdAt) }}
        </template>

        <template v-slot:item.actions="{ item }">
          <v-btn icon="mdi-eye" size="small" variant="text" @click="viewNeed(item)"></v-btn>
          <v-btn icon="mdi-file-document-check-outline" size="small" variant="text" @click="validateNeed(item)"></v-btn>
          <v-btn icon="mdi-delete" size="small" variant="text" color="error" @click="closeNeed(item)"></v-btn>
        </template>
      </v-data-table>
    </v-card>

    <!-- Create/Edit Dialog -->
    <v-dialog v-model="createNeedDialog" max-width="800px">
      <v-card>
        <v-card-title>
          {{ editingNeed ? 'Modifier la fiche de besoin' : 'Nouvelle fiche de besoin' }}
        </v-card-title>
        <v-card-text>
          <v-form ref="needForm" v-model="formValid">
            <v-row>
              <v-col cols="12" md="6">
                <v-text-field v-model="needForm.title" label="Titre du poste"
                  :rules="[v => !!v || 'Le titre est requis']" variant="outlined"></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-select v-model="needForm.department" :items="departments" label="Département"
                  :rules="[v => !!v || 'Le département est requis']" variant="outlined"></v-select>
              </v-col>
              <v-col cols="12" md="6">
                <v-select v-model="needForm.priority" :items="priorityOptions" label="Priorité"
                  :rules="[v => !!v || 'La priorité est requise']" variant="outlined"></v-select>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field v-model="needForm.positions" label="Nombre de postes" type="number"
                  :rules="[v => !!v || 'Le nombre de postes est requis']" variant="outlined"></v-text-field>
              </v-col>
              <v-col cols="12">
                <v-textarea v-model="needForm.description" label="Description du besoin"
                  :rules="[v => !!v || 'La description est requise']" variant="outlined" rows="4"></v-textarea>
              </v-col>
              <v-col cols="12">
                <v-textarea v-model="needForm.requirements" label="Compétences requises" variant="outlined"
                  rows="3"></v-textarea>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="closeDialog">
            Annuler
          </v-btn>
          <v-btn color="primary" :loading="saving" :disabled="!formValid" @click="saveNeed">
            {{ editingNeed ? 'Modifier' : 'Créer' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- PDF Viewer Dialog -->
    <v-dialog v-model="pdfDialog" max-width="900px">
      <v-card>
        <v-card-title>Téléchargement PDF</v-card-title>
        <v-card-text style="height: 80vh;">
          <iframe v-if="pdfUrl" :src="pdfUrl" width="100%" height="100%" style="border: none;"></iframe>
        </v-card-text>
        <v-card-actions>
          <v-btn @click="downloadPdfFromModal">Télécharger le PDF</v-btn>
          <v-spacer></v-spacer>
          <v-btn @click="pdfDialog = false">Fermer</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Upload Dialog -->
    <v-dialog v-model="uploadDialog" max-width="600px">
      <v-card>
        <v-card-title>
          Importer une fiche de besoin
        </v-card-title>
        <v-card-text>
          <v-form ref="uploadForm" v-model="uploadFormValid">
            <v-row>
              <v-col cols="12">
                <v-file-input v-model="uploadFile" label="Sélectionner un fichier" accept=".pdf,.doc,.docx"
                  prepend-icon="mdi-file-document" variant="outlined" :rules="[v => !!v || 'Le fichier est requis']"
                  show-size></v-file-input>
              </v-col>
              <v-col cols="12">
                <v-text-field v-model="uploadFormData.manager" label="Manager" prepend-icon="mdi-account-tie"
                  variant="outlined" :rules="[v => !!v || 'Le manager est requis']"></v-text-field>
              </v-col>
              <v-col cols="12">
                <v-select v-model="uploadFormData.department" :items="departments" label="Département"
                  prepend-icon="mdi-office-building" variant="outlined"
                  :rules="[v => !!v || 'Le département est requis']"></v-select>
              </v-col>
            </v-row>
            <v-alert v-if="uploadError" type="error" class="mt-4" closable @click:close="uploadError = ''">
              {{ uploadError }}
            </v-alert>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="closeUploadDialog">
            Annuler
          </v-btn>
          <v-btn color="primary" :loading="uploading" :disabled="!uploadFile || !uploadFormValid" @click="uploadNeed">
            Importer
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script setup>
import { useHRStore } from '@/stores/hr'
import { useSnackbarStore } from '@/stores/snackbar'
import { format } from 'date-fns'
import { fr } from 'date-fns/locale'
import { computed, onMounted, ref } from 'vue'

const hrStore = useHRStore()
const snackbarStore = useSnackbarStore()

const loading = computed(() => hrStore.isLoading)
const needs = computed(() => hrStore.needs)

const saving = ref(false)
const createNeedDialog = ref(false)
const editingNeed = ref(null)
const formValid = ref(false)
const needForm = ref({})

const searchTerm = ref('')
const selectedStatus = ref('')
const selectedDepartment = ref('')

const headers = [
  { title: 'Titre', key: 'title', sortable: true },
  { title: 'Département', key: 'department', sortable: true },
  { title: 'Manager', key: 'manager', sortable: true },
  { title: 'Statut', key: 'status', sortable: true },
  { title: 'Créé le', key: 'createdAt', sortable: true },
  { title: 'Postes', key: 'positions', sortable: true },
  { title: 'Actions', key: 'actions', sortable: false }
]

const statusOptions = [
  { title: 'En attente de validation', value: 'EN_ATTENTE_DE_VALIDATION' },
  { title: 'Validé', value: 'VALIDE' },
  { title: 'Traité', value: 'TRAITE' },
  { title: 'Fermé', value: 'FERME' }
]


const departments = [
  'IT', 'Management', 'Commercial', 'Marketing', 'RH', 'Finance', 'Opérations'
]

const filteredNeeds = computed(() => {
  let filtered = needs.value
  console.log('Filtered needs:', filtered)

  if (searchTerm.value) {
    filtered = filtered.filter(need =>
      need.title.toLowerCase().includes(searchTerm.value.toLowerCase()) ||
      need.description.toLowerCase().includes(searchTerm.value.toLowerCase())
    )
  }

  if (selectedStatus.value) {
    filtered = filtered.filter(need => need.status === selectedStatus.value)
  }

  if (selectedDepartment.value) {
    filtered = filtered.filter(need => need.department === selectedDepartment.value)
  }

  return filtered
})

const getStatusColor = (status) => {
  const colors = {
    EN_ATTENTE_DE_VALIDATION: 'blue',
    VALIDE: 'success',
    TRAITE: 'warning',
    FERME: 'error'
  }
  return colors[status] || 'grey'
}

const getStatusText = (status) => {
  const texts = {
    EN_ATTENTE_DE_VALIDATION: 'En attente de validation',
    VALIDE: 'Validée',
    TRAITE: 'Traitée',
    FERME: 'Fermée'
  }
  return texts[status] || status
}

const getPriorityColor = (priority) => {
  const colors = {
    low: 'success',
    medium: 'warning',
    high: 'error',
    critical: 'purple'
  }
  return colors[priority] || 'grey'
}

const getPriorityText = (priority) => {
  const texts = {
    low: 'Faible',
    medium: 'Moyenne',
    high: 'Élevée',
    critical: 'Critique'
  }
  return texts[priority] || priority
}

const formatDate = (date) => {
  if (!date) return '-'
  try {
    return format(new Date(date), 'dd/MM/yyyy', { locale: fr })
  } catch (e) {
    return '-'
  }
}

const resetFilters = () => {
  searchTerm.value = ''
  selectedStatus.value = ''
  selectedDepartment.value = ''
}

const pdfDialog = ref(false)
const pdfUrl = ref('')

const uploadDialog = ref(false)
const uploadFile = ref(null)
const uploading = ref(false)
const uploadForm = ref(null)
const uploadError = ref('')
const uploadFormValid = ref(false)
const uploadFormData = ref({
  manager: '',
  department: ''
})

const viewNeed = async (need) => {
  try {
    const response = await hrStore.downloadNeedPdf(need.id)
    const blob = new Blob([response.data], { type: 'application/pdf' })
    pdfUrl.value = URL.createObjectURL(blob)
    pdfDialog.value = true
  } catch (error) {
    snackbarStore.error('Erreur lors du téléchargement du PDF')
  }
}

const downloadPdf = (need) => {
  const link = document.createElement('a')
  link.href = pdfUrl.value
  link.download = `fiche_besoin_${need.id}.pdf`
  link.click()
}

const downloadPdfFromModal = () => {
  if (!pdfUrl.value) return
  const link = document.createElement('a')
  link.href = pdfUrl.value
  link.download = 'fiche_besoin.pdf'
  link.click()
}

const validateNeed = async (need) => {
  if (confirm('Êtes-vous sûr de vouloir valider cette fiche de besoin ?')) {
    try {
      await hrStore.validateNeed(need.id)
      snackbarStore.success('Fiche de besoin validée')
    } catch (error) {
      snackbarStore.error('Erreur lors de la validation')
    }
  }
}

const closeNeed = async (need) => {
  if (confirm('Êtes-vous sûr de vouloir fermer cette fiche de besoin ?')) {
    try {
      await hrStore.closeNeed(need.id)
      snackbarStore.success('Fiche de besoin fermée')
    } catch (error) {
      snackbarStore.error('Erreur lors de la fermeture')
    }
  }
}

const editNeed = (need) => {
  editingNeed.value = need
  needForm.value = { ...need }
  createNeedDialog.value = true
}

const deleteNeed = async (need) => {
  if (confirm('Êtes-vous sûr de vouloir supprimer cette fiche de besoin ?')) {
    try {
      // TODO: Call API to delete need
      const index = needs.value.findIndex(n => n.id === need.id)
      if (index > -1) {
        needs.value.splice(index, 1)
      }
      snackbarStore.success('Fiche de besoin supprimée')
    } catch (error) {
      console.error('Error deleting need:', error)
      snackbarStore.error('Erreur lors de la suppression')
    }
  }
}

const saveNeed = async () => {
  if (!formValid.value) return

  saving.value = true
  try {
    if (editingNeed.value) {
      // Update existing need
      const index = needs.value.findIndex(n => n.id === editingNeed.value.id)
      if (index > -1) {
        needs.value[index] = { ...needForm.value }
      }
      snackbarStore.success('Fiche de besoin modifiée')
    } else {
      // Create new need
      const newNeed = {
        ...needForm.value,
        id: Date.now(),
        status: 'draft',
        createdAt: new Date()
      }
      needs.value.push(newNeed)
      snackbarStore.success('Fiche de besoin créée')
    }

    closeDialog()
  } catch (error) {
    console.error('Error saving need:', error)
    snackbarStore.error('Erreur lors de la sauvegarde')
  } finally {
    saving.value = false
  }
}

const closeDialog = () => {
  createNeedDialog.value = false
  editingNeed.value = null
  needForm.value = {}
}

const closeUploadDialog = () => {
  uploadDialog.value = false
  setTimeout(() => {
    uploadFormValid.value = false
    uploadFile.value = null
    uploadError.value = ''
    uploadFormData.value = {
      manager: '',
      department: ''
    }
    if (uploadForm.value) {
      uploadForm.value.resetValidation()
    }
  }, 300)
}

const uploadNeed = async () => {
  if (!uploadFile.value || !uploadFormValid.value) {
    uploadError.value = 'Veuillez remplir tous les champs requis'
    return
  }

  uploading.value = true
  uploadError.value = ''

  try {
    await hrStore.uploadNeed(uploadFile.value, uploadFormData.value)
    snackbarStore.success('Fiche de besoin importée avec succès')

    uploading.value = false
    closeUploadDialog()

    await hrStore.fetchNeeds()
  } catch (error) {
    console.error('Error uploading need:', error)
    uploadError.value = error.message || 'Erreur lors de l\'importation'
    snackbarStore.error('Erreur lors de l\'importation')
    uploading.value = false
  }
}

onMounted(async () => {
  try {
    await hrStore.fetchNeeds()
  } catch (e) {
    snackbarStore.error('Erreur lors du chargement des besoins')
  }
})
</script>

<style scoped>
.cursor-pointer {
  cursor: pointer;
}
</style>
